"use client"
import { useState, useEffect, useRef } from "react"
import "./AISolutions.css"
import BlueSectionDivider from "./BlueSectionDivider"
import { Link } from "react-router-dom"

const numbers = [
  { num: "+20", text: "Industry sectors" },
  { num: "78", text: "Customer NPS score" },
  { num: "99%", text: "Recommendation rate" },
  { num: "+300", text: "Projects delivered" },
]

const cards = [
  {
    id: 1,
    img: "/adoption.webp?height=60&width=60",
    title: "AI ADOPTION PROCESS",
  },
  {
    id: 2,
    img: "/audit.webp?height=60&width=60",
    title: "DATA AUDIT PROCESS",
  },
  {
    id: 3,
    img: "/planning.webp?height=60&width=60",
    title: "STRATEGIC PLANNING",
  },
  {
    id: 4,
    img: "/intelligence.webp?height=60&width=60",
    title: "BUSINESS INTELLIGENCE",
  },
  {
    id: 5,
    img: "/optimization.webp?height=60&width=60",
    title: "OPTIMISATION",
  },
  {
    id: 6,
    img: "/time-series.webp?height=60&width=60",
    title: "TIME SERIES FORECASTING",
  },
  {
    id: 7,
    img: "/vision.webp?height=60&width=60",
    title: "COMPUTER VISION",
  },
  {
    id: 8,
    img: "/nlp.webp?height=60&width=60",
    title: "NATURAL LANGUAGE PROCESSING",
  },
  {
    id: 9,
    img: "/recommender.webp?height=60&width=60",
    title: "RECOMMENDER SYSTEMS",
  },
  {
    id: 10,
    img: "/rl.webp?height=60&width=60",
    title: "REINFORCEMENT LEARNING",
  },
  {
    id: 11,
    img: "/automation.webp?height=60&width=60",
    title: "AUTOMATION",
  },
]

const AISolutions = (props) => {
  const [active, setActive] = useState("solutions") // State for this navbar's active link
  const cardRefs = useRef([])
  const headingRef = useRef(null)
  const paraRef = useRef(null)

  // Define section IDs for the ai-solutions-navbar highlighting based on actual section IDs in this component
  // Currently, only the main section has an ID. If you add more sections with IDs, include them here.
  const sectionsToHighlight = [
    "solutions",
    "process",
    "benefits",
    "team",
    "tech-stack",
    "faq",
    "contact-us", // This is the ID of the main section
    // Add other section IDs here if they exist within AISolutions.jsx
    // e.g., "process", "benefits", etc.
  ]

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.2 },
    )
    ;[...cardRefs.current, headingRef.current, paraRef.current].forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [])

  // Effect for the ai-solutions-navbar highlighting
  useEffect(() => {
    const handleScroll = () => {
      // Approximate height of this ai-solutions-navbar
      // You might need to adjust this value based on the actual height of your navbar
      const aiSolutionsNavbarHeight = 60
      const scrollPosition = window.scrollY + aiSolutionsNavbarHeight + 20 // Add a small buffer

      for (let i = sectionsToHighlight.length - 1; i >= 0; i--) {
        const section = document.getElementById(sectionsToHighlight[i])
        if (section && section.offsetTop <= scrollPosition) {
          // The section ID "solutions" directly matches the navbar link "solutions"
          setActive(sectionsToHighlight[i])
          break
        }
      }
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll() // Call once to set initial state

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleClick = (id) => {
    setActive(id) // Set active state to the clicked navbar item text
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <>
    <div className="ai-solutions-navbar">
        <ul>
          {["solutions", "process", "benefits", "team", "tech stack", "faq", "contact us"].map((item) => (
            <li key={item}>
              <a onClick={() => handleClick(item)} className={active === item ? "active" : ""}>
                {item.toUpperCase()}
              </a>
            </li>
          ))}
        </ul>
      </div>
    <section className="ai-solutions-section" id="solutions">
      
      <div className="content">
        <div className="numbers-row">
          {numbers.map((n) => (
            <div key={n.num} className="number-block">
              <div className="big-num">{n.num}</div>
              <div className="num-text">{n.text}</div>
            </div>
          ))}
        </div>
        <BlueSectionDivider />
        <div className="ai-solutions-header">
          <h2 ref={headingRef} className="fade-in-left">
            OUR <br />
            <span className="blue">SOLUTIONS</span>
          </h2>
          <p className="ai-solutions-intro fade-in-right" ref={paraRef}>
            Our expertise covers an array of areas, from simpler projects such as website building to highly complex web
            based applications powered by data science, AI development, and machine learning.
          </p>
        </div>
        <div className="ai-solutions-cards">
          {cards.map((card, idx) => (
            <div key={card.id} className="ai-solution-card fade-up" ref={(el) => (cardRefs.current[idx] = el)}>
              <img src={card.img || "/placeholder.svg"} alt="icon" className="card2-icon" />
              <h3>{card.title}</h3>
            </div>
          ))}
        </div>
      </div>
      <div className="down-btn">
        <Link to="/industries">
          <button>SEE INDUSTRIES WE WORK IN</button>
        </Link>
      </div>
      {/* Placeholder sections for other navbar links if they were to exist */}
      {/* <section id="process" style={{ height: "500px", background: "#e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", marginTop: "50px" }}><h2>PROCESS Section</h2></section> */}
      {/* <section id="benefits" style={{ height: "500px", background: "#d0d0d0", display: "flex", alignItems: "center", justifyContent: "center", marginTop: "50px" }}><h2>BENEFITS Section</h2></section> */}
      {/* ... and so on for team, tech stack, faq, contact us */}
    </section>
    </>
  )
}

export default AISolutions
